import { ReactNode, useEffect, useReducer, useCallback, useRef } from "react";
import { AppNotificationTypeEnum } from "../app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../app-notification-context/UseAppNotificationContext";
import { useTranslation } from 'react-i18next';
import OfflineDetectionService from "../../services/offline-detection-service/OfflineDetectionService";
import { useUserContext } from "../user-context/UserContext";
import settings from "../../app-settings.json";
import { IDataRefreshMsg } from "../../common/models/IDataRefreshMsg";
import signalRHubConfiguration, { SignalRHubEnum } from "../../hooks/use-signalr/SignalRHubInfo";
import { useSignalR } from "../../hooks/use-signalr/UseSignalR";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useOfflineData } from "../../queries/offline-config/UseOfflineData";
import { useOfflineConfig } from "../../queries/offline-config/UseOfflineConfig";
import { useLocation } from "react-router";
import { getRouteFromPath } from "../../common/utilities/RouteHelpers";
import { RouteContextType } from "../../hooks/use-route-context/UseRouteContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { getOfflineFallbackRoute } from "./OfflineDetectContextUtils";
import OfflineDataService from "../../services/offline-data-service/OfflineDataService";
//import { getTimeframeTimeDisplayText } from "../../common/utilities/TimeframeUtilities";
//import { TimeframeTypeEnum } from "../../common/enums/TimeframeTypeEnum";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { sleep } from "../../common/utilities/Sleep";
import useNavigateTo from "../../hooks/use-navigate-to/useNavigateTo";
import { useOfflineGroups } from "../../queries/offline-config/UseOfflineGroups";
import { OfflineDetectAction, OfflineDetectActionType, OfflineDetectState } from "./OfflineDetectContextTypes";
import { OfflineDetectContext } from "./OfflineDetectContext";

const inBrowser = typeof navigator !== "undefined";

export type OfflineDetectContextProviderProps = {
    children: ReactNode;
};

const defaultOfflineState: OfflineDetectState = {
    offline: inBrowser && !navigator.onLine,
    offlineDataLoaded: false,
    forceOffline: undefined
};

function offlineDetectReducer(state: OfflineDetectState, action: OfflineDetectAction): OfflineDetectState {

    switch (action.type) {
        case OfflineDetectActionType.OfflineChanged:
            return {
                ...state,
                offline: action.payload
            };

        case OfflineDetectActionType.ForceOfflineChanged:
            return {
                ...state,
                forceOffline: action.payload,
                offline: action.payload
            };


        case OfflineDetectActionType.OfflineDataLoaded:

            return {
                ...state,
                offlineDataLoaded: action.payload
            };

        default: {
            return state;
        }
    }
}

export function OfflineDetectContextProvider({ children }: OfflineDetectContextProviderProps) {

    const [userState] = useUserContext();
    const [offlineState, dispatchOfflineState] = useReducer(offlineDetectReducer, defaultOfflineState);
    const offlineDataTimerIDRef = useRef<number | undefined>(undefined);
    const offlineDataNeededRef = useRef<boolean>(false);

    const { refetch: refreshOfflineData/*, remove */} = useQueryNotifications(useOfflineData)({
        customerID: userState.customer?.id as number,
        enabled: false,
        onSuccess: (data) => {
            if (data) {
                //console.log('offline data reloaded');
                dispatchOfflineState({
                    type: OfflineDetectActionType.OfflineDataLoaded,
                    payload: true
                });
            }
        }
    });

    const { refetch: refreshOfflineGroups} = useQueryNotifications(useOfflineGroups)({
        customerID: userState.customer?.id as number,
        enabled: false,
        onSuccess: (data) => {
            if (data) {

                //console.log('offline data reloaded');
                dispatchOfflineState({
                    type: OfflineDetectActionType.OfflineDataLoaded,
                    payload: true
                });
            }
        }
    });



    const { data: offlineConfig } = useQueryNotifications(useOfflineConfig)({});

    const [, setAppNotificationState] = useAppNotificationContext();
    const { t } = useTranslation();

    const location = useLocation();
    const navigateTo = useNavigateTo();
    const routeContext = useRouteContext();

    const hubConfiguration = signalRHubConfiguration.find(o => o.id === SignalRHubEnum.DataRefresh);

    const stopOfflineDataTimer = useCallback(() => {
        if (offlineDataTimerIDRef.current) {
            //console.log("stopOfflineDataTimer() called");
            window.clearInterval(offlineDataTimerIDRef.current);
            offlineDataTimerIDRef.current = undefined;
        }
    },[]);

    const startOfflineDataTimer = useCallback(() => {
        //console.log("startOfflineDataTimer() called");
        stopOfflineDataTimer();

        offlineDataTimerIDRef.current = window.setInterval(async () => {
            //if we need offline data, get it
            if (offlineDataNeededRef.current) {
                //console.log("getting offline data at end of interval");
                offlineDataNeededRef.current = false;
                refreshOfflineData();
            }
            //else kill the timer
            else {
                stopOfflineDataTimer();
            }
        }, settings.offlineDataMinInterval);
    }, [refreshOfflineData, stopOfflineDataTimer]);

    const delayRefreshOfflineData = useCallback(async () => {
        //let's make sure that we don't initiate a call due to a data refresh message
        //while we are delaying or for the configured interval after this call
        startOfflineDataTimer();
        await sleep(settings.initialOfflineDataLoadDelay);
        refreshOfflineData();
    }, [refreshOfflineData, startOfflineDataTimer]);
    
    const onGroupDataRefresh = () => {

        //console.log('refreshofflinegroups') 
        // we cannot wait for offline data be refreshed on an interval because it is used as
        // the front end group cache, so refresh the group data.
        refreshOfflineGroups();

        // //if the timer is running, set flag to indicate we want data
        // if (offlineDataTimerIDRef.current) {
        //     //console.log("onGroupDataRefresh() setting data needed flag true");
        //     offlineDataNeededRef.current = true;
        // }
        // //else if the timer is not running, get offline data right now
        // else {
        //     //console.log("onGroupDataRefresh() retrieving data now");

        //     //clear the needed flag
        //     offlineDataNeededRef.current = false;

        //     //start the timer running
        //     startOfflineDataTimer();

        //     //get the offline data
        //     refreshOfflineData();
        // }
    };

    //make sure that we kill the offline data timer
    //when we go offline
    useEffect(() => {
        //if we have gone offline, make sure we stop the timer
        if (offlineState.offline || offlineState.forceOffline || offlineConfig?.enabled !== true) {
            stopOfflineDataTimer();
        }
    }, [
        offlineState.offline,
        offlineState.forceOffline,
        offlineConfig?.enabled,
        stopOfflineDataTimer
    ]);

    //make sure that we kill the offline data timer
    //when the context component unmounts
    useEffect(() => {
        return () => {
            stopOfflineDataTimer();
        };
    }, [stopOfflineDataTimer]);

    useSignalR({
        url: `${settings.apiBaseUrl}${hubConfiguration?.path}`,
        customerID: userState.customer?.id,
        methodName: hubConfiguration?.methodName as string,
        onCallback: (value: IDataRefreshMsg) => {

            //console.log(`signalR callback: ${getTimeframeTimeDisplayText(TimeframeTypeEnum.None, new Date())}  value: ${JSON.stringify(value)}`);
            dispatchOfflineState({
                type: OfflineDetectActionType.OfflineDataLoaded,
                payload: false
            });

            if (value.objectType === eLynxObjectTypeEnum.Group && userState.customer) {
                onGroupDataRefresh();
            }
        },
        offline: offlineState.offline
    });

    // load offline data, if offline mode is enabled, when user is authenticated
    useEffect(() => {
        if (userState.authenticated && userState.customer?.id) {
            OfflineDataService.savePendingChanges(userState.customer.id)
            delayRefreshOfflineData();
        }

    }, [userState.authenticated,
        userState.customer?.id,
        dispatchOfflineState,
        delayRefreshOfflineData,
    ]);

    useEffect(() => {

        const pollingInterval = 10000;
        let pollingTimerID: number | undefined;

        const checkOnlineStatus = async (): Promise<boolean> => {

            const isOnline = await OfflineDetectionService.isOnline();

            // network connection successful and currently in offline state
            if (isOnline && offlineState.offline && offlineState.forceOffline !== true) {

                if (offlineConfig?.enabled) {
                    // display the info modal telling user back online
                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Info,
                        message: t("offlineDetect.isOnline"),
                        hideContactInfo: true,
                        show: true
                    });

                    if (userState.customer) {
                        await OfflineDataService.savePendingChanges(userState.customer?.id);

                        dispatchOfflineState({
                            type: OfflineDetectActionType.OfflineChanged,
                            payload: false
                        });
                    }
                }

                if (userState.authenticated && userState.customer) {
                    //await loadOfflineData();
                    await refreshOfflineData();
                }
            }

            return isOnline;
        };

        const stopPolling = () => {

            if (pollingTimerID) {
                window.clearInterval(pollingTimerID);
                pollingTimerID = undefined;
            }
        };


        const startPolling = () => {

            stopPolling();

            pollingTimerID = window.setInterval(async () => {

                const isOnline = await checkOnlineStatus();
                if (isOnline) {
                    stopPolling();
                }

            }, pollingInterval);
        };

        const onOnLine = async () => {

            // online does not always mean connection to the Internet. It can also just mean 
            // connection to some network. If you really want to determine the online status of the
            // browser, you should develop additional means for checking.
            // Let’s also take a look at the MDN docs reference for navigator.onLine. 
            // MDN web docs backs up the CanIUse.com information and adds additional notes.
            // “Browsers implement this property differently...you cannot assume that a true value necessarily means 
            // that the browser can access the internet. You could be getting false positives...” 

            // currently offline
            if (offlineState.offline) {
                if (!(await checkOnlineStatus())) {
                    startPolling();
                }
            }
        };

        const onOffLine = async () => {

            if (!offlineState.offline) {

                if (offlineConfig?.enabled) {
                    dispatchOfflineState({
                        type: OfflineDetectActionType.OfflineChanged,
                        payload: true
                    });

                    if (offlineState.forceOffline !== true) {
                        setAppNotificationState({
                            notificationType: AppNotificationTypeEnum.Info,
                            message: t("offlineDetect.isOffline"),
                            hideContactInfo: true,
                            show: true
                        });
                    }
                }
            }
        };

        window.addEventListener("online", onOnLine);
        window.addEventListener("offline", onOffLine);

        // cleanup 
        return () => {
            stopPolling();
            window.removeEventListener("online", onOnLine);
            window.removeEventListener("offline", onOffLine);
        };

    }, [
        offlineState.offline,
        offlineState.forceOffline,
        dispatchOfflineState,
        setAppNotificationState,
        offlineConfig?.enabled,
        refreshOfflineData,
        userState.authenticated,
        userState.customer,
        t
    ]);

    useEffect(() => {

        const checkOnlineStatus = async () => {
            const isOnline = await OfflineDetectionService.isOnline();

            dispatchOfflineState({
                type: OfflineDetectActionType.OfflineChanged,
                payload: !isOnline
            });


            if (isOnline) {
                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Info,
                    message: t("offlineDetect.isOnline"),
                    hideContactInfo: true,
                    show: true
                });
            }
        };

        if (offlineState.forceOffline === false) {
            checkOnlineStatus();
        }

    }, [
        offlineState.forceOffline,
        setAppNotificationState,
        t
    ]);


    useEffect(() => {

        if (offlineState.offline && userState.customer?.id) {

            // app is offline, ensure user is on a view that supports offline mode
            const currentRoute = getRouteFromPath(location.pathname);

            if (currentRoute && !currentRoute.allowOffline) {

                // view does not support offline mode, so navigate to a view that does
                // based on the current context.
                if (routeContext.groupID === undefined) {
                    routeContext.groupID = userState.user?.startGroup?.id;
                }
                
                // User Story 53209: Make Code Changes per GAS Report
                navigateToFallbackRoute();
                //console.log(`${currentRoute.name} does not support offline`);
            }
        }

        async function navigateToFallbackRoute(){
            if (userState.customer?.id !== undefined) {
                const url = await getOfflineFallbackRoute(userState.customer.id, routeContext as RouteContextType);
                navigateTo(url, {replace: true});
            }
        }
    }, [
        userState.customer?.id,
        routeContext,
        offlineState.offline,
        location.pathname,
        navigateTo,
        userState.user?.startGroup?.id
    ]);


    return (
        <OfflineDetectContext.Provider value={[offlineState, dispatchOfflineState]}>
            {children}
        </OfflineDetectContext.Provider>
    );
}